/* @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';

import { processImage } from 'js/lib/imgix';
import { tupleToStringKey } from 'js/lib/stringKeyTuple';

import { Grid, Typography, breakpoints, useTheme } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';
import type { CSSProperties } from '@coursera/coursera-ui';

import { EoiOptimizationsContextProvider } from 'bundles/expression-of-interest/components/EoiOptimizationsContext';
import type { Maybe } from 'bundles/professional-certificate-category-pages/__generated__/baseContentfulTypes';
import { EoiWidgetContainer } from 'bundles/professional-certificate-category-pages/components/degrees/EoiWidgetContainer';
import Container from 'bundles/professional-certificate-category-pages/components/shared/Container';
import HubBreadcrumbs from 'bundles/professional-certificate-category-pages/components/shared/HubBreadcrumbs';
import {
  HUB_TYPE_TO_BANNER_IMAGE,
  HubPages,
  ProductType,
} from 'bundles/professional-certificate-category-pages/constants';
import { ProductTypes } from 'bundles/university-program-qualification/utils/constants';

type Props = {
  slug?: Maybe<string>;
  pageHeading?: Maybe<string>;
  pageSubheading?: Maybe<string>;
  categoryName?: string;
  currentHubPage: (typeof HubPages)[keyof typeof HubPages];
  productType?: (typeof ProductType)[keyof typeof ProductType];
  productId?: string;
  surveyProductType?: string;
};

const styles: Record<string, CSSProperties> = {
  hubBannerWrapper: (bannerColor: string, isEOIPresent: boolean, theme: Theme) => ({
    background: bannerColor,
    width: '100vw',
    maxWidth: '100%',

    '.BannerContainer': {
      position: 'relative',
    },

    '.BannerRow': {
      display: 'flex',
      justifyContent: 'space-between',
      padding: 'var(--cds-spacing-400) 0',
      [`${breakpoints.down('md')}`]: {
        padding: 'var(--cds-spacing-150) var(--cds-spacing-200)',
      },
    },

    ...(!isEOIPresent && {
      '.BannerContentCol': {
        marginBottom: 'var(--cds-spacing-400)',
      },
    }),

    '.BannerEOICol': {
      [`${breakpoints.down('sm')}`]: {
        marginTop: 'var(--cds-spacing-150)',
        width: '100%',
      },
    },
  }),
  bannerLogoContainer: (theme: Theme) => ({
    height: '100%',
    position: 'absolute',
    width: '50%',
    top: 0,
    right: '12px',
    display: 'flex',
    justifyContent: 'flex-end',
    [`${breakpoints.down('sm')}`]: {
      display: 'none',
    },
  }),

  bannerLogo: {
    height: '100%',
    width: 'auto',
  },
};

const getBannerColor = (theme: Theme, currentHubPage: $TSFixMe) => {
  switch (currentHubPage) {
    case HubPages.MainHubPage:
      return 'var(--cds-color-blue-950)';
    case HubPages.ProductVariantHubPage:
      return 'var(--cds-color-blue-50)';
    case HubPages.DegreesCategoryHubPage:
      return 'var(--cds-color-purple-25)';
    default:
      return 'var(--cds-color-blue-950)';
  }
};

const HubBanner = ({
  slug,
  pageHeading,
  pageSubheading,
  categoryName,
  currentHubPage,
  productType,
  productId,
  surveyProductType,
}: Props) => {
  const theme = useTheme();

  if (!pageHeading) {
    return null;
  }

  const logoImage = processImage(HUB_TYPE_TO_BANNER_IMAGE[currentHubPage].toString());
  const bannerColor = getBannerColor(theme, currentHubPage);
  const headingColor = currentHubPage === HubPages.MainHubPage ? 'invertBody' : 'body';

  const completeProductId = tupleToStringKey([surveyProductType || '', productId || '']);
  const eoiProductType =
    currentHubPage === HubPages.MainHubPage ? ProductTypes.DEGREE_HUB : ProductTypes.DEGREE_CATEGORY;

  return (
    <div id="HubBanner" data-test="HubBanner" css={styles.hubBannerWrapper(bannerColor, !!productId, theme)}>
      <Container className="BannerContainer">
        <div css={styles.bannerLogoContainer}>
          <img css={styles.bannerLogo} src={logoImage} alt="" />
        </div>

        <Grid container className="BannerRow">
          <Grid item className="BannerContentCol" xs={12} md={7}>
            {productType !== ProductType.Certificates && (
              <HubBreadcrumbs productName={categoryName || pageHeading} productType={productType} />
            )}
            <Typography
              variant="d2semibold"
              component="h1"
              color={headingColor}
              css={{ margin: 'var(--cds-spacing-600) 0 var(--cds-spacing-200) 0' }}
            >
              {pageHeading}
            </Typography>
            {pageSubheading && (
              <Typography variant="h2" component="p" color={headingColor}>
                {pageSubheading}
              </Typography>
            )}
          </Grid>

          {productId && completeProductId && slug && (
            <Grid item data-testid="BannerEOIContainer" className="BannerEOICol" md={5} lg={4}>
              <EoiOptimizationsContextProvider slug={slug} productType={eoiProductType}>
                <EoiWidgetContainer productName={categoryName || pageHeading} productId={completeProductId} />
              </EoiOptimizationsContextProvider>
            </Grid>
          )}
        </Grid>
      </Container>
    </div>
  );
};

export default React.memo(HubBanner);
